import { useState, useEffect, useRef } from "react";
import ConsoleLayout from "@/components/ConsoleLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { adminApi } from "@/lib/api";
import type { AuditLog } from "@/lib/types";
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp, ShieldCheck, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { DatePicker } from "@/components/ui/date-picker";

const OUTCOME_COLORS: Record<string, string> = {
  success: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
  failure: "text-red-400 bg-red-500/10 border-red-500/20",
};

const TARGET_TYPES = ["provider", "provider_model", "science_llm"];

export default function AdminAuditLogs() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [records, setRecords] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));

  const [targetTypeFilter, setTargetTypeFilter] = useState("");
  const [outcomeFilter, setOutcomeFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [actorUserIdFilter, setActorUserIdFilter] = useState("");

  const [appliedTargetType, setAppliedTargetType] = useState("");
  const [appliedAction, setAppliedAction] = useState("");
  const [appliedActorUserId, setAppliedActorUserId] = useState("");

  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 50;

  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [justQueried, setJustQueried] = useState(false);
  const justQueriedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const applyFilters = () => {
    if (justQueried) return; // 对号显示期间忽略重复点击/回车
    setAppliedTargetType(targetTypeFilter);
    setAppliedAction(actionFilter);
    setAppliedActorUserId(actorUserIdFilter);
    setPage(0);
    setJustQueried(true);
    if (justQueriedTimer.current) clearTimeout(justQueriedTimer.current);
    justQueriedTimer.current = setTimeout(() => setJustQueried(false), 1200);
  };

  // Clear the pending 对号 timer on unmount to avoid setState-after-unmount.
  useEffect(() => () => {
    if (justQueriedTimer.current) clearTimeout(justQueriedTimer.current);
  }, []);

  const fetchLogs = async () => {
    if (!user?.is_system_admin) return;
    setLoading(true);
    setLoadError("");
    try {
      const result = await adminApi.listAuditLogs({
        page_size: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        target_type: appliedTargetType || undefined,
        outcome: outcomeFilter || undefined,
        action: appliedAction || undefined,
        actor_user_id: appliedActorUserId || undefined,
        from: new Date(startDate).toISOString(),
        to: new Date(endDate + "T23:59:59").toISOString(),
      });
      setRecords(result.records);
      setTotal(result.total);
    } catch (err) {
      setRecords([]);
      setTotal(0);
      setLoadError(err instanceof Error ? err.message : t("logs.loadFailed") || "Failed to load audit logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.is_system_admin, page, appliedTargetType, appliedAction, appliedActorUserId, outcomeFilter, startDate, endDate]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <ConsoleLayout
      title={t("admin.auditLogs.title") || "Admin Audit Logs"}
      subtitle={t("admin.auditLogs.subtitle") || "View a read-only audit trail of all admin actions"}
    >
      <div className="p-6 space-y-5">
        <div className="flex flex-col gap-2">
          {loadError && <div className="text-red-400 text-xs">{loadError}</div>}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <DatePicker value={startDate} onChange={(v) => { setStartDate(v); setPage(0); }} />
              <span className="text-xs text-slate-600">—</span>
              <DatePicker value={endDate} onChange={(v) => { setEndDate(v); setPage(0); }} />
            </div>

            <select
              value={targetTypeFilter}
              onChange={(e) => { setTargetTypeFilter(e.target.value); setPage(0); }}
              className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white h-9 focus:outline-none focus:border-blue-500/50"
            >
              <option value="">{t("admin.auditLogs.allTargetTypes") || "All Types"}</option>
              {TARGET_TYPES.map((tt) => (
                <option key={tt} value={tt}>{tt}</option>
              ))}
            </select>

            <select
              value={outcomeFilter}
              onChange={(e) => { setOutcomeFilter(e.target.value); setPage(0); }}
              className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white h-9 focus:outline-none focus:border-blue-500/50"
            >
              <option value="">{t("admin.auditLogs.filterOutcome") || "All Outcomes"}</option>
              <option value="success">{t("admin.auditLogs.outcomeSuccess") || "Success"}</option>
              <option value="failure">{t("admin.auditLogs.outcomeFailure") || "Failure"}</option>
            </select>

            <Input
              placeholder={t("admin.auditLogs.filterAction") || "Action"}
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") applyFilters(); }}
              className="bg-white/5 border-white/10 text-xs h-9 text-white placeholder:text-slate-600 w-[140px] font-mono"
            />
            <Input
              placeholder={t("admin.auditLogs.filterActorUserId") || "Actor User ID"}
              value={actorUserIdFilter}
              onChange={(e) => setActorUserIdFilter(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") applyFilters(); }}
              className="bg-white/5 border-white/10 text-xs h-9 text-white placeholder:text-slate-600 w-[160px] font-mono"
            />

            <Button size="sm" variant="ghost" onClick={applyFilters} className="relative h-9 px-3 text-xs text-slate-300 border border-white/10 active:bg-white/10">
              <span className={justQueried ? "invisible" : undefined}>{t("logs.applyFilters")}</span>
              {justQueried && (
                <span className="absolute inset-0 flex items-center justify-center text-emerald-400">
                  <Check size={14} />
                </span>
              )}
            </Button>

            <div className="ml-auto flex items-center gap-3">
              <span className="text-xs text-slate-500">{total} {t("logs.records")}</span>
              {totalPages > 1 && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage(Math.max(0, page - 1))}
                    disabled={page === 0}
                    className="p-1 rounded text-slate-500 hover:text-white disabled:opacity-30"
                  >
                    <ChevronLeft size={14} />
                  </button>
                  <span className="text-xs text-slate-400">{page + 1} / {totalPages}</span>
                  <button
                    onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                    disabled={page >= totalPages - 1}
                    className="p-1 rounded text-slate-500 hover:text-white disabled:opacity-30"
                  >
                    <ChevronRight size={14} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="sci-card overflow-hidden">
          <div className="px-5 py-3 border-b border-white/8 grid grid-cols-[130px_160px_120px_130px_90px_110px_20px] gap-3 text-[10px] text-slate-600 uppercase tracking-wider items-center">
            <div>{t("admin.auditLogs.colTime") || "Time"}</div>
            <div>{t("admin.auditLogs.colActor") || "Actor"}</div>
            <div>{t("admin.auditLogs.colAction") || "Action"}</div>
            <div>{t("admin.auditLogs.colTarget") || "Target"}</div>
            <div>{t("admin.auditLogs.colOutcome") || "Outcome"}</div>
            <div>{t("admin.auditLogs.colClientIP") || "Client IP"}</div>
            <div />
          </div>
          {records.length > 0 ? (
            records.map((log) => (
              <AuditLogRow
                key={log.id}
                log={log}
                expanded={expandedRow === log.id}
                setExpandedRow={setExpandedRow}
                t={t}
              />
            ))
          ) : (
            <div className="py-16 text-center text-slate-500 text-sm">
              {loading ? "Loading..." : t("admin.auditLogs.empty") || "No audit logs found"}
            </div>
          )}
        </div>
      </div>
    </ConsoleLayout>
  );
}

function AuditLogRow({
  log,
  expanded,
  setExpandedRow,
  t,
}: {
  log: AuditLog;
  expanded: boolean;
  setExpandedRow: (id: string | null) => void;
  t: (key: string) => string;
}) {
  const [metaExpanded, setMetaExpanded] = useState(false);

  const toggleExpand = () => {
    setExpandedRow(expanded ? null : log.id);
  };

  const hasDetail = !!log.error_code || !!log.error_message || !!log.metadata;

  return (
    <div className="border-b border-white/5 last:border-0 group transition-colors">
      <div
        className="px-5 py-3 grid grid-cols-[130px_160px_120px_130px_90px_110px_20px] gap-3 items-center cursor-pointer hover:bg-white/[0.02]"
        onClick={toggleExpand}
      >
        <div className="text-xs text-slate-400 font-mono">
          {new Date(log.created_at).toLocaleString()}
        </div>

        <div className="min-w-0">
          <div className="flex items-center gap-1">
            <span className="text-xs text-white font-mono truncate">{log.actor_user_id}</span>
            {log.actor_is_system_admin && (
              <ShieldCheck size={10} className="text-blue-400 flex-shrink-0" />
            )}
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5 truncate">{log.actor_role}</div>
        </div>

        <div className="text-xs text-slate-300 font-mono truncate">{log.action || "—"}</div>

        <div className="min-w-0">
          <div className="text-xs text-slate-400 font-mono truncate">{log.target_type || "—"}</div>
          <div className="text-[10px] text-slate-500 font-mono mt-0.5 truncate">{log.target_id || "—"}</div>
        </div>

        <div>
          <span className={cn("px-2 py-0.5 rounded text-[10px] border", OUTCOME_COLORS[log.outcome] || "text-slate-400 border-white/10")}>
            {log.outcome}
          </span>
        </div>

        <div className="text-xs text-slate-500 font-mono truncate">{log.client_ip || "—"}</div>

        <div>
          {hasDetail ? (
            expanded
              ? <ChevronUp size={14} className="text-slate-500" />
              : <ChevronDown size={14} className="text-slate-500" />
          ) : null}
        </div>
      </div>

      {expanded && hasDetail && (
        <div className="bg-black/20 p-5 border-t border-white/5 space-y-3">
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div>
              <span className="text-slate-500">{t("admin.auditLogs.colRequestID") || "Request ID"}: </span>
              <span className="text-slate-300 font-mono">{log.request_id || "—"}</span>
            </div>
            {log.actor_team_id && (
              <div>
                <span className="text-slate-500">Team ID: </span>
                <span className="text-slate-300 font-mono">{log.actor_team_id}</span>
              </div>
            )}
          </div>

          {(log.error_code || log.error_message) && (
            <div className="border-t border-white/5 pt-3">
              <div className="text-xs text-red-400 font-mono mb-1">{log.error_code || ""}</div>
              {log.error_message && (
                <div className="text-xs text-red-300/80 bg-red-950/20 p-2 rounded border border-red-900/30 font-mono whitespace-pre-wrap">
                  {log.error_message}
                </div>
              )}
            </div>
          )}

          {log.metadata && (
            <div className="border-t border-white/5 pt-3">
              <div
                className="flex items-center gap-1 text-[10px] text-slate-500 mb-1 cursor-pointer hover:text-slate-400"
                onClick={() => setMetaExpanded((v) => !v)}
              >
                {metaExpanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
                {t("admin.auditLogs.metadata") || "Metadata"}
              </div>
              {metaExpanded && (
                <pre className="text-[10px] text-slate-300 bg-white/5 p-3 rounded border border-white/10 whitespace-pre-wrap font-mono overflow-x-auto">
                  {JSON.stringify(log.metadata, null, 2)}
                </pre>
              )}
              {!metaExpanded && (
                <div className="text-[10px] text-slate-500 font-mono truncate">
                  {JSON.stringify(log.metadata).slice(0, 120)}…
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
